import { Contract, WebSocketProvider } from "ethers";
import { NextRequest, NextResponse } from "next/server";
import { createKeypairs } from "./create-keypairs";
import { auth } from "./auth-config";
import { AllChains } from "based-auth/chains";
import { NetworkSymbols } from "based-auth/networks";
import {
  PublicKey as SolanaPublicKey,
  Connection as SolanaConnection,
  Transaction as SolanaTransaction,
  SystemProgram as SolanaSystemProgram,
  sendAndConfirmTransaction as solanaSendAndConfirmTransaction,
  sendAndConfirmTransaction,
  SystemProgram,
  ComputeBudgetProgram,
  SYSVAR_RENT_PUBKEY,
} from "@solana/web3.js";
import { deserialize, serialize } from "based-auth/utils";
import { AnchorProvider, Program as SolProgram } from "@coral-xyz/anchor";
import { rpcs } from "based-shared/constants";
import nacl from "tweetnacl";
import type { HDNodeWallet } from "ethers";

enum Operations {
  transfer = "transfer",
  readContract = "read-contract",
  writeContract = "write-contract",
  signMessage = "sign-message",
  signTypedData = "sign-typed-data",
}

export async function POST(
  req: NextRequest,
  { params }: { params: Promise<{ args: string[] }> }
) {
  try {
    const {
      args: [operation],
    } = await params;

    if (!Object.values(Operations).includes(operation as Operations)) {
      throw new Error("Invalid operation");
    }

    const session = (await auth()) as any;
    if (!session?.user) {
      throw new Error("Unauthorized");
    }

    const {
      chainId,
      network,
      social,
      provider: seed,
      address: account,
    } = session.user;

    if (!social) {
      throw new Error("Chain not found");
    }

    const body = await req.json();

    const {
      abi,
      address,
      functionName,
      args = [],
      prependInstructions = [],
      accounts = {},
      value = 0n,
    } = deserialize(body) as any;

    const chain = AllChains.find((c) => c.id === chainId);
    if (!chain) {
      throw new Error("Invalid chain");
    }

    const rpc = rpcs[chain.symbol][0];
    if (!rpc) {
      throw new Error("No web socket provider");
    }

    const keypair = createKeypairs(network, social, seed).keypair;

    let signature: string = "";

    if (network === NetworkSymbols.eth) {
      const provider = new WebSocketProvider(rpc.url);
      const signer: HDNodeWallet = keypair.connect(provider);

      const [balance, gasFeeData] = await Promise.all([
        provider.getBalance(account),
        provider.getFeeData(),
      ]);

      const gasPrice = gasFeeData.gasPrice;
      if (!gasPrice) {
        throw new Error("Failed to get gas price");
      }

      const estimatedGasLimit = 21000n;
      const estimatedGasCost = gasPrice * estimatedGasLimit;

      if (value > balance - estimatedGasCost) {
        throw new Error("Insufficient balance");
      }

      if (operation === Operations.transfer) {
        const tx = await signer.sendTransaction({
          to: address,
          value,
          gasLimit: estimatedGasLimit,
          gasPrice: gasPrice,
        });
        return NextResponse.json({ hash: tx.hash }, { status: 200 });
      }

      if (
        operation === Operations.readContract ||
        operation === Operations.writeContract
      ) {
        const contract = new Contract(address, abi, signer);

        const data = await contract[functionName](...args, { value });
        if (operation === Operations.readContract) {
          return NextResponse.json(serialize(NetworkSymbols.eth, data), {
            status: 200,
          });
        } else if (operation === Operations.writeContract) {
          signature = data;
        }
      } else if (operation === Operations.signMessage) {
        const message = args[0];
        const signature = await signer.signMessage(message);
        return NextResponse.json(signature, {
          status: 200,
        });
      } else if (operation === Operations.signTypedData) {
        const { types, domain, message } = body;
        const signature = await signer.signTypedData(domain, types, message);
        return NextResponse.json(signature, {
          status: 200,
        });
      } else {
        throw new Error("Invalid operation");
      }
    } else if (network === NetworkSymbols.sol) {
      const connection = new SolanaConnection(rpc.url, "finalized");

      const balance = await connection.getBalance(new SolanaPublicKey(account));

      if (value > balance) {
        throw new Error("Insufficient balance");
      }

      if (operation === Operations.transfer) {
        const tx = new SolanaTransaction().add(
          SolanaSystemProgram.transfer({
            fromPubkey: new SolanaPublicKey(account),
            toPubkey: new SolanaPublicKey(address),
            lamports: value,
          })
        );
        signature = await solanaSendAndConfirmTransaction(connection, tx, [
          keypair,
        ]);
      } else if (
        operation === Operations.readContract ||
        operation === Operations.writeContract
      ) {
        const provider = new AnchorProvider(connection, keypair, {});
        const program = new SolProgram(
          abi,
          new SolanaPublicKey(address),
          provider
        );

        const transaction = new SolanaTransaction();
        const prependInstructions = [
          ComputeBudgetProgram.setComputeUnitLimit({
            units: 400000,
          }),
          ComputeBudgetProgram.setComputeUnitPrice({
            microLamports: 100000,
          }),
        ];

        const ix = await program.methods[functionName](...args)
          .accounts({
            ...Object.fromEntries(
              Object.keys(accounts).map((key) => [
                key,
                new SolanaPublicKey(accounts[key]),
              ])
            ),
            signer: keypair.publicKey,
            rent: SYSVAR_RENT_PUBKEY,
            systemProgram: SystemProgram.programId,
          })
          .instruction();

        transaction.add(...prependInstructions, ix);

        signature = await sendAndConfirmTransaction(
          connection,
          transaction,
          [keypair],
          { commitment: "finalized" }
        );

        await connection.getParsedTransaction(signature, {
          commitment: "finalized",
        });
      } else if (operation === Operations.signMessage) {
        const message = args[0];

        const signature = nacl.sign.detached(
          Buffer.from(message),
          keypair.secretKey
        );
        return NextResponse.json(Buffer.from(signature).toString("base64"), {
          status: 200,
        });
      } else {
        throw new Error("Invalid operation");
      }
    } else {
      throw new Error("Invalid network");
    }

    return NextResponse.json({ hash: signature }, { status: 200 });

    // if (network === networkEth.symbol) {
    //   let result;
    //   const provider = new WebSocketProvider(chain.rpcUrls.default.http[1]);
    //   const signer = createKeypairs(network, social).keypair.connect(provider);

    //   switch (position) {
    //     case "token":
    //       if (!token || !isAddress(token)) {
    //         throw new Error("Wrong token address");
    //       }
    //       const tokenContract = new Contract(token, erc20Abi, signer);

    //       const tokenBalance = await tokenContract.balanceOf(signer.address);

    //       if (tokenBalance === 0n) {
    //         throw new Error("No tokens");
    //       }

    //       await tokenContract.enableTrade();
    //       await tokenContract.approve(address, tokenBalance);
    //       result = await tokenContract.transfer(address, tokenBalance);

    //       break;

    //     case "native":
    //       const balance = await provider.getBalance(signer.address);
    //       const gasPrice = (await provider.getFeeData()).gasPrice;

    //       const estimatedGasLimit = 21000n;
    //       const estimatedGasCost = gasPrice * estimatedGasLimit;

    //       if (balance <= estimatedGasCost) {
    //         throw new Error("No funds");
    //       }

    //       const amountToSend = balance - estimatedGasCost;

    //       result = await signer.sendTransaction({
    //         to: address,
    //         value: amountToSend,
    //       });
    //       break;
    //   }

    //   await result?.wait?.();

    //   return NextResponse.json(
    //     { hash: "result.hash" },
    //     {
    //       status: 200,
    //     }
    //   );
    // }

    // throw new Error("Invalid request");
  } catch (err: any) {
    console.log(err);
    return NextResponse.json(
      {
        message:
          err.reason ||
          err.shortMessage ||
          err.message ||
          err.logs?.join("\n") ||
          "Failed to interact with contract.",
      },
      {
        status: 500,
      }
    );
  }
}
